import _ from 'lodash';
import api from '../../api';
import {
  dispatchesQueries,
  mutatesQueries,
  mutatesSchema,
  resetsState,
  updatePagination,
} from '../helper';

const default_query = {
  order_by: 'created_at',
  dir: 'desc',
};

const initialSchema = {
  occupants_amount: {
    id: 'occupants_amount',
    enabled: true,
    validation: ['required', 'integer'],
    default: 1,
    type: 'integer',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.occupants_amount_1', value: 1, text: '1 person' },
      { id: 'tenant.occupants_amount_2', value: 2, text: '2 persons' },
      { id: 'tenant.occupants_amount_3', value: 3, text: '3 persons' },
      { id: 'tenant.occupants_amount_4', value: 4, text: '4 persons' },
      { id: 'tenant.occupants_amount_5', value: 5, text: '5 persons' },
      { id: 'tenant.occupants_amount_6', value: 6, text: '6 persons' },
      { id: 'tenant.occupants_amount_7', value: 7, text: '7 persons' },
      { id: 'tenant.occupants_amount_8', value: 8, text: '8 persons' },
      { id: 'tenant.occupants_amount_9', value: 9, text: '9+ persons' },
    ],
    title: 'I am / we are',
    group: 'tenant.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_gender: {
    id: 'tenant_gender',
    enabled: true,
    validation: ['required'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.tenant_gender_select', value: null, text: 'Select' },
      { id: 'tenant.tenant_gender_male', value: 'male', text: 'Male(s)' },
      { id: 'tenant.tenant_gender_female', value: 'female', text: 'Female(s)' },
      { id: 'tenant.tenant_gender_mixed', value: 'mixed', text: 'Group/mixed' },
    ],
    title: 'Gender',
    group: 'tenant.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  property_type: {
    id: 'property_type',
    enabled: true,
    validation: ['required'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.property_type_room', value: 'room', text: 'a room' },
      { id: 'tenant.property_type_apartment', value: 'apartment', text: 'an apartment' },
    ],
    title: 'Looking for',
    group: 'tenant.group_get_started',
    required: true,
    update: true,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  room_amount: {
    id: 'room_amount',
    enabled: false,
    validation: ['required_if:property_type,room'],
    default: null,
    type: 'integer',
    presentation: 'select',
    is_dynamic: true,
    options: [
      { id: 'tenant.room_amount_1', value: 1, text: '1 room' },
      { id: 'tenant.room_amount_2', value: 2, text: '2 rooms' },
      { id: 'tenant.room_amount_3', value: 3, text: '3 rooms' },
      { id: 'tenant.room_amount_4', value: 4, text: '4 rooms' },
      { id: 'tenant.room_amount_5', value: 5, text: '5 rooms' },
      { id: 'tenant.room_amount_6', value: 6, text: '6 rooms' },
      { id: 'tenant.room_amount_7', value: 7, text: '7 rooms' },
      { id: 'tenant.room_amount_8', value: 8, text: '8 rooms' },
      { id: 'tenant.room_amount_9', value: 9, text: '9+ rooms' },
    ],
    title: 'Room amount',
    group: 'tenant.group_get_started',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  room_size: {
    id: 'room_size',
    enabled: false,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'radio',
    is_dynamic: true,
    options: [
      { id: 'tenant.room_size_any', value: null, text: "Doesn't matter" },
      { id: 'tenant.room_size_single', value: 'single', text: 'Single room' },
      { id: 'tenant.room_size_double', value: 'double', text: 'Double room' },
    ],
    title: 'Room size',
    group: 'tenant.group_get_started',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  location: {
    id: 'location',
    enabled: true,
    validation: ['required', 'location'],
    default: null,
    type: 'location',
    presentation: 'location',
    is_dynamic: false,
    options: null,
    title: 'Located in',
    group: 'tenant.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  available_from: {
    id: 'available_from',
    enabled: true,
    validation: ['required'],
    default: '2019-10-18',
    type: 'date',
    presentation: 'date',
    is_dynamic: false,
    options: null,
    title: 'I am / we are looking from',
    group: 'tenant.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  price_max: {
    id: 'price_max',
    enabled: true,
    validation: ['required', 'not_in:null', 'numeric', 'min:1', 'max:999999'],
    default: null,
    type: 'float',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'My budget is',
    group: 'tenant.group_get_started',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  price_currency: {
    id: 'price_currency',
    enabled: true,
    validation: null,
    default: 'usd',
    type: 'string',
    presentation: 'hidden',
    is_dynamic: false,
    options: null,
    title: 'Currency',
    group: 'tenant.group_get_started',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_age: {
    id: 'tenant_age',
    enabled: true,
    validation: ['numeric', 'min:18', 'max:99'],
    default: null,
    type: 'integer',
    presentation: 'number',
    is_dynamic: false,
    options: null,
    title: 'Age',
    group: 'tenant.group_about_you',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_occupation: {
    id: 'tenant_occupation',
    enabled: true,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.tenant_occupation_not_disclosed', value: null, text: 'Not disclosed' },
      { id: 'tenant.tenant_occupation_student', value: 'student', text: 'Student' },
      { id: 'tenant.tenant_occupation_professional', value: 'professional', text: 'Professional' },
    ],
    title: 'Occupation',
    group: 'tenant.group_about_you',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_smoking: {
    id: 'tenant_smoking',
    enabled: true,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.tenant_smoking_not_disclosed', value: null, text: 'Not disclosed' },
      { id: 'tenant.tenant_smoking_yes', value: 'yes', text: 'Yes' },
      { id: 'tenant.tenant_smoking_no', value: 'no', text: 'No' },
    ],
    title: 'Smoking',
    group: 'tenant.group_about_you',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_pets: {
    id: 'tenant_pets',
    enabled: true,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.tenant_pets_not_disclosed', value: null, text: 'Not disclosed' },
      { id: 'tenant.tenant_pets_yes', value: 'yes', text: 'Yes' },
      { id: 'tenant.tenant_pets_no', value: 'no', text: 'No' },
    ],
    title: 'Do you have any pets?',
    group: 'tenant.group_about_you',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  tenant_language: {
    id: 'tenant_language',
    enabled: true,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: false,
    options: [
      { id: 'tenant.tenant_language_not_disclosed', value: null, text: 'Not disclosed' },
      { id: 'tenant.tenant_language_polish', value: 'polish', text: 'Polish' },
      { id: 'tenant.tenant_language_english', value: 'english', text: 'English' },
      { id: 'tenant.tenant_language_russian', value: 'russian', text: 'Russian' },
      { id: 'tenant.tenant_language_ukrainian', value: 'ukrainian', text: 'Ukrainian' },
      { id: 'tenant.tenant_language_german', value: 'german', text: 'German' },
      { id: 'tenant.tenant_language_spanish', value: 'spanish', text: 'Spanish' },
      { id: 'tenant.tenant_language_other', value: 'other', text: 'Other' },
    ],
    title: 'Spoken language',
    group: 'tenant.group_about_you',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_gender: {
    id: 'flatmate_gender',
    enabled: false,
    validation: null,
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      { id: 'tenant.flatmate_gender_any', value: null, text: 'Any' },
      { id: 'tenant.flatmate_gender_male', value: 'male', text: 'Male' },
      { id: 'tenant.flatmate_gender_female', value: 'female', text: 'Female' },
    ],
    title: 'Gender',
    group: 'tenant.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_age_min: {
    id: 'flatmate_age_min',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'integer',
    presentation: 'number',
    is_dynamic: true,
    options: null,
    title: 'Minimal age',
    group: 'tenant.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_age_max: {
    id: 'flatmate_age_max',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'integer',
    presentation: 'number',
    is_dynamic: true,
    options: null,
    title: 'Maximal age',
    group: 'tenant.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_occupation: {
    id: 'flatmate_occupation',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      { id: 'tenant.flatmate_occupation_not_disclosed', value: null, text: 'Not disclosed' },
      { id: 'tenant.flatmate_occupation_student', value: 'student', text: 'Student' },
      {
        id: 'tenant.flatmate_occupation_professional',
        value: 'professional',
        text: 'Professional',
      },
      { id: 'tenant.flatmate_occupation_other', value: 'other', text: 'Other' },
      { id: 'tenant.flatmate_occupation_mixed', value: 'mixed', text: 'Mixed' },
    ],
    title: 'Occupation',
    group: 'tenant.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_smoking: {
    id: 'flatmate_smoking',
    enabled: false,
    validation: null,
    default: 'no',
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      { id: 'tenant.flatmate_smoking_yes', value: 'yes', text: 'Yes' },
      { id: 'tenant.flatmate_smoking_no', value: 'no', text: 'No' },
    ],
    title: 'Smoking',
    group: 'tenant.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  flatmate_pets: {
    id: 'flatmate_pets',
    enabled: false,
    validation: ['nullable'],
    default: null,
    type: 'string',
    presentation: 'select',
    is_dynamic: true,
    options: [
      { id: 'tenant.flatmate_pets_any', value: null, text: 'Any' },
      { id: 'tenant.flatmate_pets_yes', value: 'yes', text: 'Yes' },
      { id: 'tenant.flatmate_pets_no', value: 'no', text: 'No' },
    ],
    title: 'Pets',
    group: 'tenant.group_new_flatmate_or_tenant',
    required: false,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  title: {
    id: 'title',
    enabled: true,
    validation: ['required', 'min:10', 'max:120'],
    default: null,
    type: 'string',
    presentation: 'text',
    is_dynamic: false,
    options: null,
    title: 'Advert title',
    group: 'tenant.group_advert_details',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
  description: {
    id: 'description',
    enabled: true,
    validation: ['required', 'min:10', 'max:1000'],
    default: null,
    type: 'string',
    presentation: 'textarea',
    is_dynamic: false,
    options: null,
    title: 'Description',
    group: 'tenant.group_advert_details',
    required: true,
    update: false,
    has_children: false,
    has_parent: false,
    class: null,
    value: null,
  },
};

const state = () => ({
  query: default_query,
  instance: null,
  pagination: null,

  schema: initialSchema,

  // B
  fetching: false,
  fetched: false,
  // R
  instance_loading: false,
  instance_loaded: false,
  // E-A
  instance_saving: false,
  instance_saved: false,
  // D
  instance_deleting: false,
  instance_deleted: false,
  // Additional
  // @todo actually can be replaced with saving.
  instance_publishing: false,
  instance_published: false,
});

const actions = {
  ...dispatchesQueries(default_query),

  fetch: function(context) {
    const query = context.state.query;
    const promise = api.preference.my(query);
    context.commit('handleStatus', { fetching: true });
    promise
      .then((response) => {
        context.commit('handlePaginationResults', response.data);
        context.commit('handleStatus', { fetched: true });
      })
      .catch((error) => {
        context.commit('handleStatus', { fetched: false });
      })
      .then(() => {
        context.commit('handleStatus', { fetching: false });
      });
  },

  create: function(context) {
    const promise = api.preference.create(context.state.instance);
    context.commit('handleStatus', { instance_loading: true });
    context.commit('handleStatus', { instance_saved: false });
    promise
      .then((response) => {
        context.commit('handleSchemaLoaded', response.data);
        context.commit('handleInititalizingInstanceFromSchema', response.data);
        context.commit('handleStatus', { instance_loaded: true });
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_loaded: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_loading: false });
      });
  },

  edit: function(context, payload) {
    const uuid = payload;
    const instance_params = context.state.instance;
    const promise = api.preference.edit(uuid, instance_params);
    context.commit('handleStatus', { instance_loading: true });
    context.commit('handleStatus', { instance_saved: false });
    console.log('loadded start');
    promise
      .then((response) => {
        context.commit('handleSchemaLoaded', response.data);
        context.commit('handleInititalizingInstanceFromSchema', response.data);
        console.log('loadded', true);

        context.commit('handleStatus', { instance_loaded: true });
      })
      .catch((error) => {
        console.log('loadded err', error);

        context.commit('handleStatus', { instance_loaded: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_loading: false });
      });
  },

  store: function(context) {
    const instance_params = context.state.instance;
    const promise = api.preference.store(instance_params);
    context.commit('handleStatus', { instance_saving: true });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_saved: true });
        context.commit('handleResetInstance', {});
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_saved: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_saving: false });
      });
  },

  update: function(context, uuid) {
    const instance_params = context.state.instance;
    const promise = api.preference.update(uuid, instance_params);
    context.commit('handleStatus', { instance_saving: true });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_saved: true });
        context.commit('handleResetInstance', {});
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_saved: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_saving: false });
      });
  },

  delete: function(context, uuid) {
    const promise = api.preference.delete(uuid);
    context.commit('handleStatus', { instance_deleting: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_deleted: true });
        context.commit('handleInstanceDeleted', uuid);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_deleted: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_deleting: false });
      });
  },

  publish: function(context, uuid) {
    const promise = api.preference.subscribe(uuid);
    context.commit('handleStatus', { instance_publishing: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_published: true });
        context.commit('handleInstancePublished', uuid);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_published: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_publishing: false });
      });
  },

  unpublish: function(context, uuid) {
    const promise = api.preference.unsubscribe(uuid);
    context.commit('handleStatus', { instance_publishing: uuid });
    promise
      .then((response) => {
        context.commit('handleStatus', { instance_published: true });
        context.commit('handleInstanceUnpublished', uuid);
      })
      .catch((error) => {
        context.commit('handleStatus', { instance_published: false });
      })
      .then(() => {
        context.commit('handleStatus', { instance_publishing: false });
      });
  },

  updateInstance: function(context, payload) {
    context.commit('handleUpdateInstance', payload);
  },

  resetInstance: function(context, payload) {
    context.commit('handleResetInstance', null);
  },
};

const mutations = {
  ...mutatesQueries(),
  ...mutatesSchema(),
  ...resetsState(state),

  handleShow: function(state, data) {
    _.set(state, `item`, data);
  },

  handlePaginationResults: function(state, data) {
    _.set(state, `pagination`, data);
  },

  handleCancel: function(state) {
    _.set(state, `item`, null);
  },

  handleEdit: function(state, data) {
    _.set(state, `item`, {});
    _.set(state, `item`, data);

    _.each(data, (config, k) => {
      let key = config.id;
      let default_value = _.get(config, 'default', null);
      let current_value = _.get(config, 'value', default_value);
      current_value = current_value != null ? current_value : default_value;
      _.set(state, `instance.${key}`, current_value);
    });
  },

  handleUpdateInstance: function(state, payload) {
    _.each(payload, (value, key) => {
      if (value == null) {
        _.unset(state, `instance.${key}`);
      } else {
        _.set(state, `instance.${key}`, value);
      }
    });

    // @todo
    // Not the most elegant solution to the problem,
    // but for some reason Vuex stopped seeing changes in input
    //
    // @acthung !!! types!!!
    let schema = Object.assign({}, state.schema);
    _.each(payload, (value, key) => {
      if (_.has(schema, key)) {
        _.set(schema, `${key}.value`, value);
      }
    });
    state.schema = schema;
  },

  handleInstanceDeleted: function(state, uuid) {
    updatePagination(state, uuid, () => null);
  },

  handleInstancePublished: function(state, uuid) {
    updatePagination(state, uuid, (record) => {
      record.status = 'published';
      return record;
    });
  },

  handleInstanceUnpublished: function(state, uuid) {
    updatePagination(state, uuid, (record) => {
      record.status = 'unpublished';
      return record;
    });
  },

  handleResetInstance: function(state, payload) {
    _.set(state, 'instance', {});
  },

  handleStatus: function(state, payload) {
    _.each(payload, (value, key) => {
      _.set(state, key, value);
    });
  },
};

const getters = {};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
